// Shapes returned by the /planning routes, plus small formatters.

export type QuarterPhase = 'past' | 'current' | 'future';

export interface QuarterOption {
  id: string;
  label: string;
  year: number;
  quarter: number;
  phase: QuarterPhase;
  start: string;
  end: string;
  epics: number;
}

export interface Counts {
  tickets: number;
  shipped: number;
  ready: number;
  open: number;
  invalid: number;
  shipped_pct: number | null;
  shipped_or_ready_pct: number | null;
}

export interface CheckpointCounts extends Counts {
  /** Shipped or release-ready share at the checkpoint against the target; null until it can be judged. */
  meets_target: boolean | null;
}

export interface QuarterOverall extends Counts {
  epics: number;
  at_checkpoint: CheckpointCounts | null;
}

export interface QuarterProject extends QuarterOverall {
  project_key: string;
  project_name: string | null;
}

export interface QuarterEpic extends Counts {
  issue_key: string;
  name: string | null;
  project_key: string;
  project_name: string | null;
  jira_url: string;
  at_checkpoint: CheckpointCounts | null;
}

export interface QuarterData {
  quarter: QuarterOption & { range: { from: string; to: string } };
  as_of: string;
  checkpoint: { day: string; reached: boolean; target_pct: number; after_sprint: number };
  overall: QuarterOverall;
  projects: QuarterProject[];
  epics: QuarterEpic[];
}

export interface SprintItem {
  id: number;
  name: string;
  label: string;
  number: number | null;
  state: 'future' | 'active' | 'closed' | null;
  start: string | null;
  end: string | null;
  complete: string | null;
  is_current: boolean;
  overdue: boolean;
}

export interface SprintProject {
  project_key: string;
  project_name: string | null;
  board_id: number;
}

export interface SprintsData {
  projects: SprintProject[];
  project: SprintProject | null;
  quarters: { id: string; label: string }[];
  quarter: { id: string; label: string } | null;
  sprints: SprintItem[];
  current_sprint_id: number | null;
}

export interface ReportNumbers {
  total: number;
  committed: number;
  added: number;
  removed: number;
  completed: number;
  not_completed: number;
  completed_elsewhere: number;
  scope_creep_pct: number | null;
  threshold_pct: number;
  over_threshold: boolean | null;
}

export interface CarryRow {
  issue_key: string;
  name: string | null;
  type: string | null;
  status: string | null;
  assignee: string | null;
  project_key: string | null;
  jira_url: string;
  finished_since: boolean;
  moved_to: { id: number; name: string } | null;
  rollover_count: number | null;
  rollover_reason: string | null;
  needs_review: boolean;
}

export interface ReviewRow {
  issue_key: string;
  name: string | null;
  type: string | null;
  status: string | null;
  assignee: string | null;
  project_key: string;
  jira_url: string;
  rollover_count: number;
  rollover_reason: string | null;
}

export interface SprintDetail {
  sprint: {
    id: number;
    name: string;
    label: string;
    project_key: string | null;
    project_name: string | null;
    board_id: number | null;
    quarter: { id: string; label: string } | null;
    state: 'future' | 'active' | 'closed' | null;
    start: string | null;
    end: string | null;
    complete: string | null;
    is_current: boolean;
    overdue: boolean;
  };
  report: {
    available: boolean;
    not_started: boolean;
    planned: number;
    fetched_at: string | null;
    stale: boolean;
    error: string | null;
    numbers: ReportNumbers | null;
  };
  carry_over: {
    kind: 'carried' | 'open';
    total: number;
    finished_since: number | null;
    moved_on: number | null;
    back_to_backlog: number | null;
    by_reason: { reason: string; count: number }[];
    items: CarryRow[];
  };
  needs_review: { threshold: number; total: number; items: ReviewRow[] };
}

/** "[Q3 FY26] Saral (Misc) - Birthday dashboard" → "Saral (Misc) - Birthday dashboard" */
export function epicTitle(name: string | null | undefined): string {
  return (name ?? '')
    .replace(/\[\s*Q[1-4]\s*FY\s*(?:\d{4}|\d{2})\s*\]/gi, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/^[\s|\-–—:]+|[\s|\-–—:]+$/g, '')
    .trim() || (name ?? 'Untitled');
}

export const plural = (n: number, one: string, many: string): string => (n === 1 ? one : many);
